import React, { useState } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, ChevronDown } from 'lucide-react';
import { Card } from '../ui/card';
import { cn } from '../../lib/utils';
import type { PlanViolation } from './types';
import type { BoardModel } from './boardModel';

interface ViolationsPanelProps {
  model: BoardModel;
  onSelect: (violation: PlanViolation) => void;
}

const MAX_HEIGHT = 'max-h-64';

export const ViolationsPanel: React.FC<ViolationsPanelProps> = ({ model, onSelect }) => {
  const [open, setOpen] = useState(true);
  const { hardCount, softCount, sortedViolations } = model;
  const total = hardCount + softCount;

  if (total === 0) {
    return (
      <Card className="flex items-center gap-2 px-4 py-3 text-sm" data-testid="plan-violations">
        <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
        <span>Brak błędów i ostrzeżeń w tym planie.</span>
      </Card>
    );
  }

  return (
    <Card data-testid="plan-violations">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <div className="flex flex-wrap items-center gap-3 text-sm font-medium">
          <span>Problemy w planie</span>
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold',
              hardCount > 0
                ? 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-200'
                : 'bg-muted text-muted-foreground',
            )}
          >
            <AlertCircle className="w-3.5 h-3.5" /> Błędy: {hardCount}
          </span>
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold',
              softCount > 0
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200'
                : 'bg-muted text-muted-foreground',
            )}
          >
            <AlertTriangle className="w-3.5 h-3.5" /> Ostrzeżenia: {softCount}
          </span>
        </div>
        <ChevronDown className={cn('w-4 h-4 shrink-0 transition-transform', !open && '-rotate-90')} />
      </button>
      {open && (
        <ul className={cn('overflow-y-auto border-t divide-y', MAX_HEIGHT)}>
          {sortedViolations.map((v, i) => {
            const locatable = !!(v.employeeId || v.positionId);
            const hard = v.severity === 'HARD';
            const content = (
              <>
                {hard ? (
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
                ) : (
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                )}
                <span className="min-w-0">
                  {v.message}
                </span>
              </>
            );
            return (
              <li key={`${v.code}-${v.employeeId ?? ''}-${v.positionId ?? ''}-${v.shiftTimeId ?? ''}-${v.date ?? ''}-${i}`}>
                {locatable ? (
                  <button
                    type="button"
                    onClick={() => onSelect(v)}
                    className="flex w-full items-start gap-2 px-4 py-2 text-left text-sm hover:bg-muted"
                  >
                    {content}
                  </button>
                ) : (
                  <div className="flex items-start gap-2 px-4 py-2 text-sm">{content}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
};
